import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, HostListener, Inject, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserServiceInterface } from '@hutsix/ngxh6';

export interface SidebarNavItem {
    route?: string;
    label: string;
    svgIcon: string;
    roles?: string[];
    matchExact?: boolean;
    query?: { [key: string]: string };
    children?: SidebarChildItem[];
    expanded?: boolean;
}

export interface SidebarChildItem {
    route?: string;
    label: string;
    roles?: string[];
    children?: SidebarGrandchildItem[];
    expanded?: boolean;
}

export interface SidebarGrandchildItem {
    route: string;
    label: string;
    roles?: string[];
}

@Component({
    selector: 'app-sidebar[nav]',
    templateUrl: './app-sidebar.component.html',
    styleUrls: ['./app-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppSidebarComponent implements OnInit {
    @Input() public nav: SidebarNavItem[] = [];
    @Input() public logo?: string;
    @Input() public hidden?: boolean;
    @Output() public collapsedChange = new EventEmitter<boolean>();

    public toggled = false;
    public toggledLarge = false;
    public collapsed = false;
    public expandedItems: Set<string> = new Set(['Dashboard']); // Dashboard expanded by default

    private readonly COLLAPSE_BREAKPOINT = 1024;

    constructor(
        @Inject('UserService') public user: UserServiceInterface,
        public router: Router,
        public cdRef: ChangeDetectorRef,
    ) {}

    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        if (this.hidden !== undefined) this.toggledLarge = this.hidden;
        this.checkScreenSize();
        this.expandActiveSection(this.router.url);
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(e => this.expandActiveSection(e.urlAfterRedirects));
    }

    /** Open the section holding the current page, so its sub-pages are visible on arrival. */
    private expandActiveSection(url: string): void {
        const path = url.split(/[?#]/)[0];
        const section = this.nav.find(
            item => item.children?.length && item.route && item.route !== '/' && (path === item.route || path.startsWith(item.route + '/')),
        );
        if (section && !this.expandedItems.has(section.label)) {
            this.expandedItems.add(section.label);
            this.cdRef.markForCheck();
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        this.checkScreenSize();
    }

    private checkScreenSize(): void {
        const shouldCollapse = window.innerWidth < this.COLLAPSE_BREAKPOINT;
        if (this.collapsed !== shouldCollapse) {
            this.collapsed = shouldCollapse;
            this.collapsedChange.emit(this.collapsed);
            this.cdRef.markForCheck();
        }
    }

    toggleCollapse(): void {
        this.collapsed = !this.collapsed;
        this.collapsedChange.emit(this.collapsed);
        this.cdRef.markForCheck();
    }

    toggleSidebar(): void {
        this.toggled = !this.toggled;
    }

    toggleSidebarLarge(): void {
        this.toggledLarge = !this.toggledLarge;
    }

    toggleExpanded(label: string): void {
        if (this.expandedItems.has(label)) {
            this.expandedItems.delete(label);
        } else {
            this.expandedItems.add(label);
        }
        this.cdRef.markForCheck();
    }

    expandItem(label: string): void {
        this.expandedItems.add(label);
        this.cdRef.markForCheck();
    }

    isExpanded(label: string): boolean {
        return this.expandedItems.has(label);
    }

    closeSidebar(delay?: number): void {
        if (!delay) {
            this.toggled = false;
            return;
        }

        setTimeout(() => {
            this.toggled = false;
            this.cdRef.detectChanges();
        }, delay);
    }
}
